import React, { useState, useEffect } from 'react';
import { 
  Save, Edit, Eye, Plus, Trash2, X, Upload, Image, Users, Target, Award, CheckCircle,
  Globe, Zap, Code, Smartphone, Palette, Database, Cloud, Shield, Settings, Mail, Phone, MapPin,
  ShoppingBag, Star, Download, Headphones, RefreshCw, Calculator, AlertCircle, ArrowRight
} from 'lucide-react';
import { quotePageAPI } from '../../services/api';
import AdminSectionWrapper from './AdminSectionWrapper';

const QuotePageManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [quoteData, setQuoteData] = useState({
    page_title: 'Get Your Project Quote',
    page_description: 'AI-powered estimation tool to get instant quotes for your project',
    calculator_icon: 'Calculator',
    cta_title: 'Ready to Start Your Project?',
    cta_description: 'Let\'s discuss your project requirements and create something amazing together',
    cta_button1_text: 'Start Your Project',
    cta_button1_link: '/contact',
    cta_button2_text: 'Get Free Quote',
    cta_button2_link: '/quote'
  });

  const [activeSection, setActiveSection] = useState('header');
  const [isEditing, setIsEditing] = useState(false);

  const sections = [
    { id: 'header', label: 'Page Header', icon: Calculator },
    { id: 'cta', label: 'Call to Action', icon: CheckCircle }
  ];

  // Icon mapping for calculator icon
  const getIconComponent = (iconName) => {
    const icons = {
      'Calculator': Calculator,
      'Code': Code,
      'Smartphone': Smartphone,
      'Globe': Globe,
      'ShoppingCart': ShoppingBag,
      'Palette': Palette,
      'Database': Database,
      'Cloud': Cloud,
      'Shield': Shield,
      'Zap': Zap,
      'Star': Star,
      'CheckCircle': CheckCircle,
      'ArrowRight': ArrowRight,
      'Download': Download,
      'Send': Mail,
      'Clock': AlertCircle,
      'DollarSign': Target,
      'Users': Users
    };
    return icons[iconName] || Calculator;
  };

  // Fetch quote page data
  useEffect(() => {
    fetchQuoteData();
  }, []);

  const fetchQuoteData = async () => {
    try {
      setLoading(true);
      const response = await quotePageAPI.getContent();
      if (response?.data?.success) {
        const data = response.data.data;
        setQuoteData({
          page_title: data.page_title || 'Get Your Project Quote',
          page_description: data.page_description || 'AI-powered estimation tool to get instant quotes for your project',
          calculator_icon: data.calculator_icon || 'Calculator',
          cta_title: data.cta_title || 'Ready to Start Your Project?',
          cta_description: data.cta_description || 'Let\'s discuss your project requirements and create something amazing together',
          cta_button1_text: data.cta_button1_text || 'Start Your Project',
          cta_button1_link: data.cta_button1_link || '/contact',
          cta_button2_text: data.cta_button2_text || 'Get Free Quote',
          cta_button2_link: data.cta_button2_link || '/quote'
        });
      }
    } catch (err) {
      console.error('Error fetching quote data:', err);
      setError(`Error loading quote data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setQuoteData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveQuoteData = async () => {
    try {
      setSaving(true);
      await quotePageAPI.updateContent(quoteData);
      setError(null);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving quote data:', err);
      setError(`Error saving quote data: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderHeaderSection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Page Header Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Page Title
        </label>
        <input
          type="text"
          value={quoteData.page_title}
          onChange={(e) => handleInputChange('page_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Get Your Project Quote"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Page Description
        </label>
        <textarea
          value={quoteData.page_description}
          onChange={(e) => handleInputChange('page_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="AI-powered estimation tool to get instant quotes for your project"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Calculator Icon
        </label>
        <select
          value={quoteData.calculator_icon}
          onChange={(e) => handleInputChange('calculator_icon', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="Calculator">Calculator</option>
          <option value="Code">Code</option>
          <option value="Smartphone">Smartphone</option>
          <option value="Globe">Globe</option>
          <option value="ShoppingCart">Shopping Cart</option>
          <option value="Palette">Palette</option>
          <option value="Database">Database</option>
          <option value="Cloud">Cloud</option>
          <option value="Shield">Shield</option>
          <option value="Zap">Zap</option>
          <option value="Star">Star</option>
          <option value="CheckCircle">Check Circle</option>
          <option value="ArrowRight">Arrow Right</option>
          <option value="Download">Download</option>
          <option value="Send">Send</option>
          <option value="Clock">Clock</option>
          <option value="DollarSign">Dollar Sign</option>
          <option value="Users">Users</option>
        </select>
      </div>

      {/* Preview */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</h4>
        <div className="text-center">
          {(() => {
            const IconComponent = getIconComponent(quoteData.calculator_icon);
            return <IconComponent className="w-8 h-8 mx-auto mb-2 text-primary-600" />;
          })()}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {quoteData.page_title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {quoteData.page_description}
          </p>
        </div>
      </div>
    </div>
  );

  const renderCTASection = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Call to Action Section</h3>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Title
        </label>
        <input
          type="text"
          value={quoteData.cta_title}
          onChange={(e) => handleInputChange('cta_title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Ready to Start Your Project?"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={quoteData.cta_description}
          onChange={(e) => handleInputChange('cta_description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Let's discuss your project requirements and create something amazing together"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 1 Text
          </label>
          <input
            type="text"
            value={quoteData.cta_button1_text}
            onChange={(e) => handleInputChange('cta_button1_text', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Start Your Project"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 1 Link
          </label>
          <input
            type="text"
            value={quoteData.cta_button1_link}
            onChange={(e) => handleInputChange('cta_button1_link', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="/contact"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 2 Text
          </label>
          <input
            type="text"
            value={quoteData.cta_button2_text}
            onChange={(e) => handleInputChange('cta_button2_text', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="Get Free Quote"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Button 2 Link
          </label>
          <input
            type="text"
            value={quoteData.cta_button2_link}
            onChange={(e) => handleInputChange('cta_button2_link', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="/quote"
          />
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'header':
        return renderHeaderSection();
      case 'cta':
        return renderCTASection();
      default:
        return renderHeaderSection();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <AdminSectionWrapper>
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
                <Calculator className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
                Quote Page Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Manage quote page content and sections</p>
            </div>
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={saveQuoteData}
                    disabled={saving}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Content
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 m-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <span className="text-red-700 dark:text-red-400">{error}</span>
        </div>
      )}

      {/* Content */}
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 min-h-screen">
          <div className="p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Sections</h2>
            <nav className="space-y-2">
              {sections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    {section.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-6">
          <div className="max-w-4xl">
            {renderContent()}
          </div>
        </div>
      </div>
    </AdminSectionWrapper>
  );
};

export default QuotePageManagement; 